import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats } from '../services/dashboardService';
import { ApiErrorResponse } from '../services/authService';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { DashboardResponse } from '../types/dashboard';
import styles from './DashboardPage.module.css';
import layoutStyles from '../styles/layout.module.css';

const COLORS = ['#FABE06', '#4A90E2', '#50C878', '#FF6B6B', '#9B59B6', '#F39C12', '#1ABC9C', '#304b9c'];

const AnimatedCounter: React.FC<{ value: number; duration?: number; decimals?: number }> = ({
  value,
  duration = 2000,
  decimals = 0,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const currentCount = progress * value;
      setCount(currentCount);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{count.toFixed(decimals)}</span>;
};

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: string;
  colorIndex: number;
  decimals?: number;
  sub?: string;
}> = ({ title, value, icon, colorIndex, decimals = 0, sub }) => {
  const colorClass = `statCardColor${colorIndex % 10}` as keyof typeof styles;
  const valueColorClass = `statValueColor${colorIndex % 10}` as keyof typeof styles;
  return (
    <div className={`${styles.statCard} ${styles[colorClass]}`} title={title}>
      <div className={styles.statIcon} title={title}>{icon}</div>
      <div className={styles.statContent}>
        <h3 className={styles.statTitle} title={title}>{title}</h3>
        <p className={`${styles.statValue} ${styles[valueColorClass]}`} title={String(value)}>
          <AnimatedCounter value={value} decimals={decimals} />
        </p>
        {sub && <p className={styles.statSub} title={sub}>{sub}</p>}
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  const [data, setData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getDashboardStats();
        setData(response);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        setError(apiError.message || 'Failed to load dashboard statistics.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Access denied. Admin privileges required.</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.loadingContainer}>
              <p>Loading dashboard...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.errorMessage}>{error}</p>
          </main>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    stats,
    storesByCategory,
    usersByRole,
    recentStores,
    reviewsSummary,
    productsByPriceRange,
    storesByLocation,
    adsByStatus,
    topStoresByProducts,
  } = data;

  const storeStatusData = [
    { name: 'Active', value: stats.stores.active, color: '#50C878' },
    { name: 'Pending', value: stats.stores.pending, color: '#FABE06' },
  ].filter((d) => d.value > 0);

  const adsStatusData = [
    { name: 'Active', value: adsByStatus.active, color: '#50C878' },
    { name: 'Inactive', value: adsByStatus.inactive, color: '#FF6B6B' },
  ].filter((d) => d.value > 0);

  const hasPriceData = productsByPriceRange.some((p) => p.count > 0);

  return (
    <div className={styles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <div className={styles.headerSection}>
            <h1 className={styles.storeTitle} title="Admin Dashboard">Admin Dashboard</h1>
            <p className={styles.storeSubtitle} title="Platform Overview & Analytics">Platform Overview & Analytics</p>
          </div>

          <div className={styles.statsGrid}>
            <StatCard
              title="Stores"
              value={stats.stores.total}
              icon="🏪"
              colorIndex={0}
              sub={`${stats.stores.active} active, ${stats.stores.pending} pending`}
            />
            <StatCard title="Categories" value={stats.categories} icon="📂" colorIndex={1} />
            <StatCard title="Sectors" value={stats.sectors} icon="📊" colorIndex={2} />
            <StatCard title="Products" value={stats.products} icon="📦" colorIndex={3} />
            <StatCard title="Advertisements" value={stats.advertisements} icon="📢" colorIndex={4} />
            <StatCard title="Users" value={stats.users} icon="👥" colorIndex={5} />
            <StatCard title="Locations" value={stats.locations} icon="📍" colorIndex={6} />
            <StatCard title="SubLocations" value={stats.subLocations} icon="🗺️" colorIndex={7} />
            <StatCard
              title="Total Reviews"
              value={reviewsSummary.totalReviews}
              icon="⭐"
              colorIndex={8}
            />
            <StatCard
              title="Avg. Rating"
              value={reviewsSummary.averageRating}
              icon="🌟"
              colorIndex={9}
              decimals={1}
            />
          </div>

          <div className={styles.chartsGrid}>
            {storesByCategory.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Stores by Category">Stores by Category</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <BarChart
                      data={storesByCategory.map((c) => ({ name: c.category_name, value: c.count }))}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#FABE06" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {storeStatusData.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Store Status">Store Status</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <PieChart>
                      <Pie
                        data={storeStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {storeStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {usersByRole.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Users by Role">Users by Role</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <PieChart>
                      <Pie
                        data={usersByRole.map((r) => ({ name: r.role_name, value: r.count }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {usersByRole.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {hasPriceData && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Products by Price Range">Products by Price Range</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <BarChart
                      data={productsByPriceRange}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#4A90E2" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {storesByLocation.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Stores by Location">Stores by Location</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <BarChart
                      data={storesByLocation.map((l) => ({
                        name: l.locationName,
                        value: l.count,
                      }))}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#50C878" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {adsStatusData.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Advertisement Status">Advertisement Status</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <PieChart>
                      <Pie
                        data={adsStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {adsStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          <div className={styles.chartsGrid}>
            {topStoresByProducts.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle} title="Top Stores by Product Count">Top Stores by Product Count</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <BarChart
                      data={topStoresByProducts.map((s) => ({
                        name: s.storeName.length > 15 ? s.storeName.slice(0, 15) + '…' : s.storeName,
                        value: s.productCount,
                        fullName: s.storeName,
                      }))}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip
                        formatter={(value: number, name: string, props: { payload?: { fullName?: string } }) => [
                          value,
                          props?.payload?.fullName || 'Products',
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="value" fill="#9B59B6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          <div className={styles.tableSection}>
            <h3 className={styles.tableTitle} title="Recent Stores">Recent Stores</h3>
            {recentStores.length > 0 ? (
              <table className={styles.recentStoresTable}>
                <thead>
                  <tr>
                    <th title="Store Name">Store Name</th>
                    <th title="Status">Status</th>
                    <th title="Action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentStores.map((store) => (
                    <tr key={store.id}>
                      <td title={store.store_name}>{store.store_name}</td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${
                            store.is_active ? styles.statusActive : styles.statusPending
                          }`}
                          title={store.is_active ? 'Active' : 'Pending'}
                        >
                          {store.is_active ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={
                            store.is_active
                              ? `/store/${store.id}/overview`
                              : '/store-requests'
                          }
                          className={styles.storeLink}
                          title={store.is_active ? 'View Store' : 'View Request'}
                        >
                          {store.is_active ? 'View Store' : 'View Request'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className={styles.noDataMessage} title="No stores found.">No stores found.</p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
